// src/pages/admin/UserLoginModuleDetails.jsx
// ------------------------------------------------------------
// React version of: UserLoginModuleDetails.aspx
// Requirements:
// - Server-side pagination + search (date filter) for large data
// - Print should print ONLY current page records (pageSize) + applied filter
// - Excel is already working (kept as Node endpoint open/download)
// - Use <input type="date"> (UI) but send dd/MM/yyyy to server
// - No popup window for print (use hidden iframe print -> avoids popup blocker)
// ------------------------------------------------------------

import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";

import HeaderBar from "../components/HeaderBar";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Printer,
  FileSpreadsheet,
  Search,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  Eye,
} from "lucide-react";

import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";

const BASE_URL = "https://nissi-india.com";

// ---------- helpers ----------
function getFinYearId() {
  try {
    const direct = localStorage.getItem("finYearId");
    if (direct) return String(direct);
    const admin = JSON.parse(localStorage.getItem("admin") || "null");
    if (admin?.finYearId) return String(admin.finYearId);
  } catch (_) {}
  return "";
}

function tenantHeaders() {
  const finYearId = getFinYearId();
  return finYearId ? { "x-fin-year-id": finYearId } : {};
}

function tenantParams(extra = {}) {
  const finYearId = getFinYearId();
  return finYearId ? { ...extra, finYearId } : extra;
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

// dd/MM/yyyy -> yyyy-MM-dd for input[type=date]
function dmyToISO(dmy) {
  if (!dmy) return "";
  const [dd, mm, yyyy] = String(dmy).split("/");
  if (!dd || !mm || !yyyy) return "";
  return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
}

// yyyy-MM-dd -> dd/MM/yyyy for server
function isoToDMY(iso) {
  if (!iso) return "";
  const [yyyy, mm, dd] = String(iso).split("-");
  if (!yyyy || !mm || !dd) return "";
  return `${dd}/${mm}/${yyyy}`;
}

/**
 * Print current page without popup windows:
 * - create hidden iframe
 * - write a simple HTML document with table + inline CSS
 * - call print() inside iframe
 */
function printRowsInIframe({
  title = "User Log Modules Report",
  companyName = "",
  userName = "",
  dateText = "",
  rows = [],
}) {
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow?.document;
  if (!doc) {
    document.body.removeChild(iframe);
    return;
  }

  const safe = (v) =>
    String(v ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");

  const html = `
<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${safe(title)}</title>
  <style>
    * { font-family: Arial, Helvetica, sans-serif; }
    body { padding: 18px; color: #111; }
    .h1 { font-size: 20px; font-weight: 700; text-align: center; margin: 0 0 6px 0; }
    .h2 { font-size: 16px; font-weight: 700; text-align: center; margin: 0 0 14px 0; }
    .meta { display: flex; justify-content: space-between; margin: 12px 0 10px; font-size: 12px; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    th, td { border: 1px solid #111; padding: 6px 8px; vertical-align: top; }
    th { background: #f1f5f9; font-weight: 700; }
    .center { text-align: center; }
    @media print {
      body { padding: 0; }
      .no-print { display: none; }
    }
  </style>
</head>
<body>
  ${companyName ? `<div class="h1">${safe(companyName)}</div>` : ""}
  <div class="h2">${safe(title)}</div>

  <div class="meta">
    <div><b>User Name:</b> ${safe(userName)}</div>
    <div><b>Date:</b> ${safe(dateText || "-")}</div>
  </div>

  <table>
    <thead>
      <tr>
        <th class="center" style="width:70px;">S.No</th>
        <th style="width:120px;">Date</th>
        <th style="width:120px;">InTime</th>
        <th>ModuleName</th>
      </tr>
    </thead>
    <tbody>
      ${
        rows.length
          ? rows
              .map(
                (r, idx) => `
        <tr>
          <td class="center">${idx + 1}</td>
          <td>${safe(r.fld_date)}</td>
          <td>${safe(r.fld_intime)}</td>
          <td>${safe(r.fld_modulename)}</td>
        </tr>
      `
              )
              .join("")
          : `
        <tr>
          <td class="center" colspan="4">No Records Found</td>
        </tr>
      `
      }
    </tbody>
  </table>
</body>
</html>
`;

  doc.open();
  doc.write(html);
  doc.close();

  setTimeout(() => {
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();

    setTimeout(() => {
      try {
        document.body.removeChild(iframe);
      } catch {}
    }, 300);
  }, 250);
}

export default function UserLoginModuleDetails() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();

  const logId = sp.get("logId") || "";

  const { skin, isDark } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const headerBgClass = `bg-${baseColor}-${baseShade}`;
  const headerTextClass = "text-white";
  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryBtnHover = `hover:bg-${baseColor}-700`;

  const controlClass =
    "w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder-gray-400 disabled:bg-gray-100 disabled:text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-300";

  const lightBtnClass =
    "inline-flex items-center gap-2 rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 hover:bg-slate-50 disabled:opacity-50";

  const pageBtnClass =
    "inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-3 py-1 text-gray-900 disabled:opacity-50";

  const [metaLoading, setMetaLoading] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [userName, setUserName] = useState("");
  const [dateISO, setDateISO] = useState("");

  const dateDMY = useMemo(() => isoToDMY(dateISO), [dateISO]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  );

  const latestReq = useRef(0);

  const fetchMeta = async () => {
    if (!logId) return;

    setMetaLoading(true);
    try {
      const r = await axios.get(`${BASE_URL}/api/user-login-module-details/meta`, {
        headers: tenantHeaders(),
        params: tenantParams({ logId }),
      });

      const m = r.data || {};
      setCompanyName(m.companyName || "");
      setUserName(m.userName || "");
      setDateISO(dmyToISO(m.date || ""));
    } catch (e) {
      console.error(e);
      toast.error("Failed to load module header details");
    } finally {
      setMetaLoading(false);
    }
  };

  const fetchList = async (opts = {}) => {
    if (!logId) return;

    const nextPage = opts.page ?? page;
    const nextPageSize = opts.pageSize ?? pageSize;

    setLoading(true);
    const reqId = Date.now();
    latestReq.current = reqId;

    try {
      const r = await axios.get(`${BASE_URL}/api/user-login-module-details/list`, {
        headers: tenantHeaders(),
        params: tenantParams({
          logId,
          date: dateDMY || "",
          page: nextPage,
          pageSize: nextPageSize,
        }),
      });
      if (latestReq.current !== reqId) return;

      setRows(r.data?.rows || []);
      setTotal(Number(r.data?.total || 0));
      setPage(Number(r.data?.page || nextPage));
      setPageSize(Number(r.data?.pageSize || nextPageSize));
    } catch (e) {
      console.error(e);
      toast.error("Failed to load module records");
    } finally {
      if (latestReq.current === reqId) setLoading(false);
    }
  };

  useEffect(() => {
    if (!logId) {
      toast.error("logId missing. Go back and open from User Login Details.");
      return;
    }
    (async () => {
      await fetchMeta();
      await fetchList({ page: 1 });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logId]);

  const onSearch = () => {
    setPage(1);
    fetchList({ page: 1 });
  };

  const gotoPage = (p) => {
    const np = clamp(p, 1, totalPages);
    setPage(np);
    fetchList({ page: np });
  };

  const pageButtons = useMemo(() => {
    const windowSize = 5;
    const start = Math.max(1, page - Math.floor(windowSize / 2));
    const end = Math.min(totalPages, start + windowSize - 1);
    const s2 = Math.max(1, end - windowSize + 1);
    const arr = [];
    for (let i = s2; i <= end; i++) arr.push(i);
    return arr;
  }, [page, totalPages]);

  const onPrint = () => {
    printRowsInIframe({
      title: "User Log Modules Report",
      companyName,
      userName,
      dateText: dateDMY,
      rows,
    });
  };

  const onExcel = () => {
    const qs = new URLSearchParams();
    qs.set("logId", logId);
    if (dateDMY) qs.set("date", dateDMY);
    const finYearId = getFinYearId();
    if (finYearId) qs.set("finYearId", finYearId);
    window.location.href = `${BASE_URL}/api/user-login-module-details/excel?${qs.toString()}`;
  };

  return (
    <AdminLayoutWrapper>
      <HeaderBar />

      <div className={`p-4 ${isDark ? "text-white" : "text-gray-900"}`}>
        <ToastContainer />

        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/adminMaster/UserLoginDetails")}
              className={lightBtnClass}
              title="Back"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>

            <h1 className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
              User Login Module Details
            </h1>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onPrint}
              className={lightBtnClass}
              title="Print current page"
              disabled={loading}
            >
              <Printer className="h-4 w-4" />
              Print
            </button>

            <button
              type="button"
              onClick={onExcel}
              className={lightBtnClass}
              title="Export Excel"
              disabled={loading}
            >
              <FileSpreadsheet className="h-4 w-4" />
              Excel
            </button>

            <select
              className="rounded border border-gray-300 bg-white px-2 py-2 text-sm text-gray-900"
              value={pageSize}
              onChange={(e) => {
                const ps = Number(e.target.value);
                setPageSize(ps);
                setPage(1);
                fetchList({ page: 1, pageSize: ps });
              }}
              disabled={loading}
              title="Rows per page"
            >
              {[10, 25, 50, 100, 200].map((n) => (
                <option key={n} value={n} className="bg-white text-gray-900">
                  {n}/page
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Filters */}
        <div className="rounded border bg-white p-4 text-gray-900">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-800">Date</label>
              <input
                type="date"
                className={controlClass}
                value={dateISO}
                onChange={(e) => setDateISO(e.target.value)}
                disabled
              />
              {metaLoading && (
                <div className="mt-1 text-xs text-slate-500">Loading…</div>
              )}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-800">UserName</label>
              <input
                className={controlClass}
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                disabled
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={onSearch}
                disabled={loading}
                className={`inline-flex items-center gap-2 rounded px-4 py-2 text-white disabled:opacity-50 ${primaryBtnClass} ${primaryBtnHover}`}
              >
                <Search className="h-4 w-4" />
                {loading ? "Searching..." : "Search"}
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="mt-4 rounded border bg-white text-gray-900">
          <div className="max-h-[60vh] overflow-auto">
            <table className="min-w-[900px] w-full border-collapse">
              <thead className="sticky top-0 z-10">
                <tr className={`${headerBgClass} ${headerTextClass} text-sm`}>
                  <th className="border border-white/20 px-2 py-2 text-center">Slno</th>
                  <th className="border border-white/20 px-2 py-2">Date</th>
                  <th className="border border-white/20 px-2 py-2">InTime</th>
                  <th className="border border-white/20 px-2 py-2">ModuleName</th>
                  <th className="border border-white/20 px-2 py-2 text-center">Pages</th>
                </tr>
              </thead>

              <tbody className="text-sm text-gray-900">
                {loading ? (
                  [...Array(8)].map((_, i) => (
                    <tr key={i}>
                      <td className="border px-2 py-3" colSpan={5}>
                        <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
                      </td>
                    </tr>
                  ))
                ) : rows.length === 0 ? (
                  <tr>
                    <td className="px-3 py-6 text-center text-slate-500" colSpan={5}>
                      No Records Found
                    </td>
                  </tr>
                ) : (
                  rows.map((r, idx) => (
                    <tr key={`${r.fld_moduleid}-${idx}`} className="text-gray-900 hover:bg-slate-50">
                      <td className="border px-2 py-2 text-center">
                        {(page - 1) * pageSize + (idx + 1)}
                      </td>
                      <td className="border px-2 py-2">{r.fld_date}</td>
                      <td className="border px-2 py-2">{r.fld_intime}</td>
                      <td className="border px-2 py-2">{r.fld_modulename}</td>
                      <td className="border px-2 py-2 text-center">
                        <button
                          className="inline-flex items-center gap-2 rounded bg-slate-900 px-3 py-1 text-xs text-white"
                          onClick={() =>
                            navigate(
                              `/adminMaster/UserLoginModulePageDetails?moduleId=${r.fld_moduleid}&date=${encodeURIComponent(
                                r.fld_date
                              )}&userName=${encodeURIComponent(userName)}&moduleName=${encodeURIComponent(
                                r.fld_modulename
                              )}`
                            )
                          }
                          title="View Pages"
                        >
                          <Eye className="h-4 w-4" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 p-3 text-sm text-gray-800">
            <div className="text-slate-600">
              Total: <span className="font-semibold">{total}</span>
              {total > 0 && (
                <>
                  {" "}
                  | Showing{" "}
                  <span className="font-semibold">{(page - 1) * pageSize + 1}</span> -{" "}
                  <span className="font-semibold">{Math.min(page * pageSize, total)}</span>
                </>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                className={pageBtnClass}
                disabled={loading || page <= 1}
                onClick={() => gotoPage(page - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
                Prev
              </button>

              {pageButtons[0] > 1 && (
                <>
                  <button className={pageBtnClass} onClick={() => gotoPage(1)} disabled={loading}>
                    1
                  </button>
                  <span className="px-1 text-slate-500">…</span>
                </>
              )}

              {pageButtons.map((p) => (
                <button
                  key={p}
                  className={`rounded border border-gray-300 px-3 py-1 ${
                    p === page ? "bg-slate-900 text-white" : "bg-white text-gray-900"
                  }`}
                  disabled={loading}
                  onClick={() => gotoPage(p)}
                >
                  {p}
                </button>
              ))}

              {pageButtons[pageButtons.length - 1] < totalPages && (
                <>
                  <span className="px-1 text-slate-500">…</span>
                  <button
                    className={pageBtnClass}
                    onClick={() => gotoPage(totalPages)}
                    disabled={loading}
                  >
                    {totalPages}
                  </button>
                </>
              )}

              <button
                className={pageBtnClass}
                disabled={loading || page >= totalPages}
                onClick={() => gotoPage(page + 1)}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        <div className={`mt-2 text-xs ${isDark ? "text-white/60" : "text-slate-500"}`}>
          Print prints only the current table page ({pageSize} rows max) with the applied date filter.
        </div>
      </div>
    </AdminLayoutWrapper>
  );
}